import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export const PHONE_INVALID_MESSAGE_KEY =
  'api_mobile_phone_number_invalid_number';
export const REQUIRED_MESSAGE_KEY = 'general_label_required';

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function isValidInternationalPhoneNumberParts(
  countryCallingCode: string,
  localNumber: string
): boolean {
  const cc = digitsOnly(countryCallingCode);
  const local = digitsOnly(localNumber);
  if (!cc || !local) return false;

  const phone = parsePhoneNumberFromString(`+${cc}${local}`);
  return !!phone?.isValid();
}

// Base schema (kept as a ZodObject so other schemas can reference .shape)
export const PhoneNumberPartsSchema = z.object({
  countryCallingCode: z.string().min(1, REQUIRED_MESSAGE_KEY),
  localNumber: z.string().min(1, REQUIRED_MESSAGE_KEY),
});

// Refined schema for use where .shape access is not needed
export const PhoneNumberPartsValidatorSchema =
  PhoneNumberPartsSchema.superRefine((value, ctx) => {
    if (
      !isValidInternationalPhoneNumberParts(
        value.countryCallingCode,
        value.localNumber
      )
    ) {
      ctx.addIssue({
        code: 'custom',
        message: PHONE_INVALID_MESSAGE_KEY,
        path: ['localNumber'],
      });
    }
  });

// Nullable wrapper for API DTOs that allow mobilePhoneNumber to be null
export const NullablePhoneNumberPartsValidatorSchema =
  PhoneNumberPartsValidatorSchema.nullable();

// Nullable-parts variant used by some forms (either both provided, or neither)
export const NullablePhoneNumberPartsOptionalValidatorSchema = z
  .object({
    countryCallingCode: z.string().nullable(),
    localNumber: z.string().nullable(),
  })
  .nullable()
  .superRefine((value, ctx) => {
    if (!value) return;

    const cc = value.countryCallingCode?.trim() ?? '';
    const local = value.localNumber?.trim() ?? '';

    // Both empty => treat as "not provided"
    if (!cc && !local) return;

    // One provided => require the other
    if (!cc) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE_KEY,
        path: ['countryCallingCode'],
      });
      return;
    }

    if (!local) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE_KEY,
        path: ['localNumber'],
      });
      return;
    }

    if (!isValidInternationalPhoneNumberParts(cc, local)) {
      ctx.addIssue({
        code: 'custom',
        message: PHONE_INVALID_MESSAGE_KEY,
        path: ['localNumber'],
      });
    }
  });

export const InternationalPhoneStringValidatorSchema = z
  .string()
  .min(1, REQUIRED_MESSAGE_KEY)
  .superRefine((value, ctx) => {
    const raw = value.trim();
    if (!raw) return;

    const normalized = raw.startsWith('+')
      ? `+${digitsOnly(raw)}`
      : `+${digitsOnly(raw)}`;
    const phone = parsePhoneNumberFromString(normalized);

    if (!phone?.isValid()) {
      ctx.addIssue({
        code: 'custom',
        message: PHONE_INVALID_MESSAGE_KEY,
      });
    }
  });
